import {
  GuestFormDataFields,
  GuestFormMixed,
  MapString,
  PublicMeetingType,
} from '@chilipiper/api-client/src/meeting-types'
import {
  GuestFormDataFilledMixed,
  GuestFormDataFilledDataFields,
} from '@chilipiper/api-client/src/team-scheduling'
import { RoundRobinAvailableAttendees } from '@chilipiper/api-client/src/user-availability'
import { setExtras } from '@chilipiper/sentry'
import { FormKeys } from '@chilipiper/design-system'
import {
  convertExpectedHostToDistributionHost,
  GuestFormValues,
} from '../../load-availability-options'
import {
  mapOwnershipLinkRoutingResultToAssignments,
  OwnershipLinkRoutingResult,
  UnifiedRoutingResult,
  WithDistribution,
} from '../../load-meeting-attempt'
import { isStrictRoundRobinAssignees } from '../../load-slots'
import { isMixedGuestForm } from '../types'
import {
  RoundRobinExpectedHost,
  ProductFeature,
  ScheduleMeetingRequestFeatureAdminSchedulingLink,
  ScheduleMeetingRequestFeatureRoundRobinSchedulingLink,
  ScheduleMeetingRequestFeatureOwnershipSchedulingLink,
  ScheduleMeetingRequestFeaturePersonalSchedulingLink,
  ScheduleMeetingRequestFeatureGroupSchedulingLink,
} from '../../../types'
import {
  createOwnershipLinkTimeAndAssignment,
  createRoundRobinLinkTimeAndAssignment,
} from './time-and-assignment'

type FeatureAdminSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestFeatureAdminSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type' | 'meetingAttemptId'
>

type FeatureRoundRobinSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestFeatureRoundRobinSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type' | 'meetingAttemptId'
>

type FeatureOwnershipLinkRequest = Pick<
  ScheduleMeetingRequestFeatureOwnershipSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type' | 'meetingAttemptId'
>

type FeaturePersonalSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestFeaturePersonalSchedulingLink,
  'timeAndAssignment' | 'meetingTypeId' | 'type' | 'meetingAttemptId'
>

type FeatureGroupSchedulingLinkRequest = Pick<
  ScheduleMeetingRequestFeatureGroupSchedulingLink,
  'timeAndAssignment' | 'linkId' | 'type' | 'meetingAttemptId'
>

type RequestFeature =
  | FeatureAdminSchedulingLinkRequest
  | FeatureRoundRobinSchedulingLinkRequest
  | FeatureOwnershipLinkRequest
  | FeaturePersonalSchedulingLinkRequest
  | FeatureGroupSchedulingLinkRequest

type FeaturePayload = {
  productFeature: ProductFeature
  reservationId?: string
  meetingAttemptId?: string
  slotStartAt?: string
  duration?: string
  groupSchedulingLinkHostId?: string
  selectedMeetingTypeId?: string
  expectedHost?: RoundRobinExpectedHost
  routingResult?: UnifiedRoutingResult
  availableRoundRobinAssignees?: RoundRobinAvailableAttendees[]
  /* @deprecated use routingResult instead */
  ownershipRoutingResult?: OwnershipLinkRoutingResult
  /* @deprecated use routingResult instead */
  roundRobinRoutingResult?: WithDistribution
}

export const getMeetingRequestFeature = ({
  productFeature,
  reservationId,
  duration,
  slotStartAt,
  groupSchedulingLinkHostId,
  meetingAttemptId,
  selectedMeetingTypeId,
  expectedHost,
  availableRoundRobinAssignees,
  routingResult,
  /* @deprecated use routingResult instead */
  ownershipRoutingResult,
  /* @deprecated use routingResult instead */
  roundRobinRoutingResult,
}: FeaturePayload): RequestFeature => {
  const meetingTypeId =
    selectedMeetingTypeId ??
    ('selectedMeetingTypeId' in productFeature ? productFeature.selectedMeetingTypeId : undefined)

  if (productFeature.type === 'AdminIndividualSchedulingLink' && reservationId) {
    const adminSchedulingLinkFeature: FeatureAdminSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: productFeature?.hostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      meetingAttemptId,
      linkId: productFeature.linkId,
      type: 'AdminSchedulingLink',
    }

    return adminSchedulingLinkFeature
  }

  if (productFeature.type === 'PersonalIndividualSchedulingLink' && reservationId) {
    const personalSchedulingLinkFeature: FeaturePersonalSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: productFeature?.hostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      meetingAttemptId,
      meetingTypeId: productFeature.meetingTypeId,
      type: 'PersonalSchedulingLink',
    }

    return personalSchedulingLinkFeature
  }

  if (productFeature.type === 'GroupSchedulingLink' && groupSchedulingLinkHostId && reservationId) {
    const groupSchedulingLinkFeature: FeatureGroupSchedulingLinkRequest = {
      timeAndAssignment: {
        hostId: groupSchedulingLinkHostId,
        reservationId: reservationId,
        type: 'WithReservation',
      },
      meetingAttemptId,
      linkId: productFeature.linkId,
      type: 'GroupSchedulingLink',
    }

    return groupSchedulingLinkFeature
  }

  if (
    productFeature.type === 'RoundRobinSchedulingLink' &&
    (isStrictRoundRobinAssignees(availableRoundRobinAssignees) ||
      availableRoundRobinAssignees?.every(
        assignee => assignee.type === 'FlexibleRoundRobin' && assignee.userIds.length === 1
      )) &&
    reservationId
  ) {
    // If all assignees in distribution are flexible but with one member only, we can convert them to strict round robin so we can use the same logic for both cases
    const assignees = availableRoundRobinAssignees.map(assignee =>
      assignee.type === 'StrictRoundRobin'
        ? assignee
        : {
            type: 'StrictRoundRobin' as const,
            distributionId: assignee.distributionId,
            userId: assignee.userIds[0],
          }
    )
    const userIds = assignees.map(assignee => assignee.userId)
    const hostId = expectedHost?.type === 'User' ? expectedHost.userId : userIds[0]

    const timeAndAssignment = createRoundRobinLinkTimeAndAssignment({
      hostId,
      reservationId,
      strictRoundRobinAssignees: assignees,
      roundRobinRoutingResult:
        routingResult?.type === 'RoundRobinSchedulingLink'
          ? routingResult.routingResult
          : roundRobinRoutingResult,
    })

    const roundRobinSingleAssignee: FeatureRoundRobinSchedulingLinkRequest = {
      timeAndAssignment,
      meetingAttemptId,
      linkId: productFeature.linkId,
      type: 'RoundRobinSchedulingLink',
    }

    return roundRobinSingleAssignee
  }

  if (
    productFeature.type === 'RoundRobinSchedulingLink' &&
    meetingTypeId &&
    slotStartAt &&
    duration &&
    availableRoundRobinAssignees &&
    expectedHost
  ) {
    const timeAndAssignment = createRoundRobinLinkTimeAndAssignment({
      slotStartsAt: slotStartAt,
      duration,
      host: convertExpectedHostToDistributionHost(expectedHost),
      meetingTypeId,
      availableAssignees: availableRoundRobinAssignees,
      roundRobinRoutingResult:
        routingResult?.type === 'RoundRobinSchedulingLink'
          ? routingResult.routingResult
          : roundRobinRoutingResult,
    })

    const roundRobinMultipleAssignees: FeatureRoundRobinSchedulingLinkRequest = {
      timeAndAssignment,
      meetingAttemptId,
      linkId: productFeature.linkId,
      type: 'RoundRobinSchedulingLink',
    }

    return roundRobinMultipleAssignees
  }

  if (
    productFeature.type === 'OwnershipSchedulingLink' &&
    (!availableRoundRobinAssignees?.length ||
      isStrictRoundRobinAssignees(availableRoundRobinAssignees)) &&
    reservationId
  ) {
    const assigneeIds = mapOwnershipLinkRoutingResultToAssignments(
      isStrictRoundRobinAssignees(availableRoundRobinAssignees)
        ? availableRoundRobinAssignees
        : undefined,
      routingResult?.type === 'OwnershipSchedulingLink'
        ? routingResult.routingResult
        : ownershipRoutingResult
    ).map(assignment => assignment.assigneeId)

    const hostId = expectedHost?.type === 'User' ? expectedHost.userId : assigneeIds[0]

    const ownershipSingleAssignee: FeatureOwnershipLinkRequest = {
      timeAndAssignment: createOwnershipLinkTimeAndAssignment({
        hostId,
        reservationId,
        strictRoundRobinAssignees: isStrictRoundRobinAssignees(availableRoundRobinAssignees)
          ? availableRoundRobinAssignees
          : undefined,
        ownershipLinkRoutingResult:
          routingResult?.type === 'OwnershipSchedulingLink'
            ? routingResult.routingResult
            : ownershipRoutingResult,
      }),
      meetingAttemptId,
      linkId: productFeature.linkId,
      type: 'OwnershipSchedulingLink',
    }

    return ownershipSingleAssignee
  }

  if (
    productFeature.type === 'OwnershipSchedulingLink' &&
    availableRoundRobinAssignees &&
    meetingTypeId &&
    slotStartAt &&
    duration &&
    expectedHost
  ) {
    const ownershipMultipleAssignees: FeatureOwnershipLinkRequest = {
      timeAndAssignment: createOwnershipLinkTimeAndAssignment({
        slotStartsAt: slotStartAt,
        duration,
        meetingTypeId,
        host: convertExpectedHostToDistributionHost(expectedHost),
        availableAssignees: availableRoundRobinAssignees,
        ownershipLinkRoutingResult:
          routingResult?.type === 'OwnershipSchedulingLink'
            ? routingResult.routingResult
            : ownershipRoutingResult,
      }),
      meetingAttemptId,
      linkId: productFeature.linkId,
      type: 'OwnershipSchedulingLink',
    }

    return ownershipMultipleAssignees
  }

  setExtras({
    meetingTypeId,
    productFeature,
    availableRoundRobinAssignees,
    slotStartAt,
    duration,
    reservationId,
    expectedHost,
  })

  throw new Error('Feature not found')
}

type FeatureAdminSchedulingLinkRequestWithGuestFormData = FeatureAdminSchedulingLinkRequest &
  Pick<ScheduleMeetingRequestFeatureAdminSchedulingLink, 'guestFormData'>

type FeatureRoundRobinSchedulingLinkRequestWithGuestFormData =
  FeatureRoundRobinSchedulingLinkRequest &
    Pick<ScheduleMeetingRequestFeatureRoundRobinSchedulingLink, 'guestFormData'>

type FeatureOwnershipLinkRequestWithGuestFormData = FeatureOwnershipLinkRequest &
  Pick<ScheduleMeetingRequestFeatureOwnershipSchedulingLink, 'guestFormData'>

type FeaturePersonalSchedulingLinkRequestWithGuestFormData = FeaturePersonalSchedulingLinkRequest &
  Pick<ScheduleMeetingRequestFeaturePersonalSchedulingLink, 'guestFormData'>

type FeatureGroupSchedulingLinkRequestWithGuestFormData = FeatureGroupSchedulingLinkRequest &
  Pick<ScheduleMeetingRequestFeatureGroupSchedulingLink, 'guestFormData'>

type RequestFeatureWithGuestFormData =
  | FeatureAdminSchedulingLinkRequestWithGuestFormData
  | FeatureRoundRobinSchedulingLinkRequestWithGuestFormData
  | FeaturePersonalSchedulingLinkRequestWithGuestFormData
  | FeatureGroupSchedulingLinkRequestWithGuestFormData
  | FeatureOwnershipLinkRequestWithGuestFormData

export const getMeetingRequestFeatureWithGuestFormData = (
  feature: RequestFeature,
  formValues: GuestFormValues,
  meetingType: PublicMeetingType
): RequestFeatureWithGuestFormData => {
  if (feature.type === 'PersonalSchedulingLink') {
    return {
      ...feature,
      guestFormData: getGuestFormData<GuestFormDataFilledMixed>(formValues, meetingType),
    }
  }

  return {
    ...feature,
    guestFormData: getGuestFormData<GuestFormDataFilledDataFields>(formValues, meetingType),
  }
}

const FORM_TYPE: Record<
  GuestFormDataFields['type'] | GuestFormMixed['type'],
  'FilledDataFields' | 'FilledMixed'
> = {
  DataFields: 'FilledDataFields',
  Mixed: 'FilledMixed',
}

type GuestFormData = GuestFormDataFilledDataFields | GuestFormDataFilledMixed

export const guestFormKeys: FormKeys<GuestFormValues> = {
  customLocation: 'customLocation',
  locationHost: 'locationHost',
  location: 'location',
  attendees: 'attendees',
}

const reservedGuestFormDataFields = [
  guestFormKeys.location,
  guestFormKeys.customLocation,
  guestFormKeys.attendees,
]

export const getGuestFormData = <T = GuestFormData>(
  formValues: GuestFormValues,
  meetingType?: PublicMeetingType
): T => {
  const formType = FORM_TYPE[meetingType?.guestForm.type ?? 'Mixed'] ?? 'FilledMixed'

  const formDataValues = Object.keys(formValues).reduce((result, key) => {
    const field = formValues[key]
    const value = Array.isArray(field) ? field.join(';') : field

    if (reservedGuestFormDataFields.includes(key)) {
      return result
    }

    return {
      ...result,
      [key]: value,
    }
  }, {} as MapString)

  if (meetingType?.guestForm.type !== 'Mixed') {
    return {
      values: formDataValues,
      type: formType,
    } as T
  }

  return Object.keys(formDataValues).reduce(
    (result, key) => {
      if (isMixedGuestForm(meetingType.guestForm)) {
        const field = meetingType?.guestForm.items.find(
          field =>
            (field.type === 'Question' && field.id === key) ||
            (field.type === 'Field' && field.reference === key)
        )

        const typeKey = field?.type === 'Question' ? 'questionValues' : 'fieldValues'

        return {
          ...result,
          [typeKey]: {
            ...result[typeKey],
            [key]: formDataValues[key],
          },
        }
      }

      return result
    },
    {
      fieldValues: {},
      questionValues: {},
      type: formType,
    }
  ) as T
}
